import { SecureStorage } from './StorageEncryption';

const VALID_THEMES = new Set(['light', 'dark', 'high-contrast', 'OS-theme']);

function resolveUsername(user) {
  if (!user) return null;
  // Prefer stable login identifiers. `name` from the API is often a display name (may contain
  // spaces). When it has spaces, derive a key from givenName.surname before the typed login id
  // so we do not reuse an old `theme_<loginId>` entry after the profile replaces `name`.
  const pref = (user.preferredName || '').trim().toLowerCase();
  if (pref) return pref;
  const rawName = (user.name || '').trim().toLowerCase();
  if (rawName && !/\s/.test(rawName)) return rawName;
  const givenName = (user.givenName || '').trim().toLowerCase();
  const surname = (user.surname || '').trim().toLowerCase();
  if (givenName && surname) return `${givenName}.${surname}`;
  const formUser = (user.username || '').trim().toLowerCase();
  if (formUser) return formUser;
  if (rawName) return rawName;
  return null;
}

export function userThemeStorageKey(user) {
  const username = resolveUsername(user);
  if (!username) return null;
  return `theme_${username}`;
}

function readThemeValueByKey(key) {
  if (!key) return null;

  // Preferred path: encrypted storage.
  const secureValue = SecureStorage.getItem(key);
  if (typeof secureValue === 'string' && VALID_THEMES.has(secureValue)) {
    return secureValue;
  }

  // Backward compatibility for any plain-text value manually set or left from older runs.
  const raw = localStorage.getItem(key);
  if (typeof raw === 'string' && VALID_THEMES.has(raw)) {
    try {
      // Normalize to encrypted storage so future reads are consistent.
      SecureStorage.setItem(key, raw);
    } catch (_) {
      /* ignore; still return raw for this read */
    }
    return raw;
  }

  return null;
}

export function loadPersistedTheme(user) {
  const key = userThemeStorageKey(user);
  if (!key) return null;
  return readThemeValueByKey(key);
}

export function hasPersistedTheme(user) {
  const key = userThemeStorageKey(user);
  if (!key) return false;
  return readThemeValueByKey(key) != null;
}

export function savePersistedTheme(user, newTheme) {
  const key = userThemeStorageKey(user);
  if (!key) return;
  if (!VALID_THEMES.has(newTheme)) return;
  try {
    SecureStorage.setItem(key, newTheme);
  } catch (e) {
    console.error('Theme save failed', e);
    throw e;
  }
}

/** Remove all localStorage entries except persisted UI theme keys (`theme_*`). */
export function clearLocalStorageExceptThemeKeys() {
  const preserved = [];
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (key && key.startsWith('theme_')) {
      preserved.push([key, localStorage.getItem(key)]);
    }
  }
  localStorage.clear();
  preserved.forEach(([key, value]) => {
    if (value != null) localStorage.setItem(key, value);
  });
}
